<script>
  import { createInviteLink } from '../../lib/services/groups-api.js';
  import { buildInviteUrl } from '../../lib/logic/groups-logic.js';

  let { groupId, onClose, onCreated } = $props();

  const EXPIRY_OPTIONS = [
    { label: 'Never', value: null },
    { label: '7 days', value: 7 },
    { label: '30 days', value: 30 },
  ];

  let expiresInDays = $state(null);
  let submitting = $state(false);
  let error = $state('');
  let generatedUrl = $state('');
  let copied = $state(false);

  async function handleCreate() {
    error = '';
    submitting = true;
    try {
      const shortCode = await createInviteLink(groupId, { expiresInDays });
      const url = buildInviteUrl(shortCode);
      generatedUrl = url;
      onCreated?.();
    } catch (err) {
      console.error('[InviteModal] Failed to create invite:', err);
      error = err.message || 'Failed to create invite link.';
    } finally {
      submitting = false;
    }
  }

  async function copyUrl() {
    try {
      await navigator.clipboard.writeText(generatedUrl);
      copied = true;
      setTimeout(() => (copied = false), 2000);
    } catch {
      // Fallback: select text
    }
  }

  function handleBackdropClick(e) {
    if (e.target === e.currentTarget) onClose();
  }

  function handleKeydown(e) {
    if (e.key === 'Escape') onClose();
  }
</script>

<svelte:window onkeydown={handleKeydown} />

<!-- svelte-ignore a11y_no_static_element_interactions -->
<div class="modal-backdrop" onclick={handleBackdropClick} onkeydown={handleKeydown}>
  <div class="modal" role="dialog" aria-modal="true" aria-labelledby="modal-title">
    <div class="modal-header">
      <h2 id="modal-title" class="modal-title">Create Invite Link</h2>
      <button class="modal-close" aria-label="Close" onclick={onClose}>
        <svg
          viewBox="0 0 24 24"
          width="20"
          height="20"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>
    </div>

    <div class="modal-body">
      {#if !generatedUrl}
        <div class="field" role="group" aria-labelledby="invite-expiry-label">
          <span id="invite-expiry-label" class="field-label">Link expires</span>
          <div class="expiry-options">
            {#each EXPIRY_OPTIONS as opt (opt.value)}
              <button
                class="expiry-btn"
                class:selected={expiresInDays === opt.value}
                onclick={() => (expiresInDays = opt.value)}
              >
                {opt.label}
              </button>
            {/each}
          </div>
          <p class="field-hint-text">Multi-use link — anyone with this link can join.</p>
        </div>

        {#if error}
          <div class="modal-error">{error}</div>
        {/if}

        <div class="modal-actions">
          <button class="btn-secondary" onclick={onClose} disabled={submitting}>Cancel</button>
          <button class="btn-primary" onclick={handleCreate} disabled={submitting}>
            {submitting ? 'Creating...' : 'Create Link'}
          </button>
        </div>
      {:else}
        <div class="invite-success">
          <div class="success-icon">🔗</div>
          <p class="success-text">Share this link to invite people to the group:</p>
          <div class="url-row">
            <input
              class="url-input"
              type="text"
              readonly
              value={generatedUrl}
              onclick={(e) => e.currentTarget.select()}
            />
            <button class="copy-btn" class:copied onclick={copyUrl}>
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
          {#if expiresInDays}
            <p class="expires-note">Expires in {expiresInDays} days</p>
          {:else}
            <p class="expires-note">Never expires</p>
          {/if}
        </div>
        <div class="modal-actions">
          <button class="btn-primary" onclick={onClose}>Done</button>
        </div>
      {/if}
    </div>
  </div>
</div>

<style>
  .modal-backdrop {
    position: fixed;
    inset: 0;
    z-index: 500;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    backdrop-filter: blur(2px);
  }

  .modal {
    background: white;
    border-radius: 16px;
    width: 100%;
    max-width: 440px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  }

  .modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 24px 0;
  }

  .modal-title {
    font-size: 1.125rem;
    font-weight: 700;
    color: #1f2937;
    margin: 0;
  }

  .modal-close {
    width: 32px;
    height: 32px;
    padding: 0;
    background: transparent;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #6b7280;
    transition: background 0.15s;
  }

  .modal-close:hover {
    background: #f3f4f6;
  }

  .modal-body {
    padding: 20px 24px 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .field {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .field-label {
    font-size: 13px;
    font-weight: 600;
    color: #374151;
  }

  .expiry-options {
    display: flex;
    gap: 8px;
  }

  .expiry-btn {
    flex: 1;
    padding: 8px;
    border: 2px solid #e5e7eb;
    border-radius: 8px;
    background: white;
    font-size: 14px;
    font-weight: 500;
    color: #374151;
    cursor: pointer;
    transition:
      border-color 0.15s,
      background 0.15s;
    font-family: inherit;
  }

  .expiry-btn.selected {
    border-color: #3b82f6;
    background: #eff6ff;
    color: #1d4ed8;
  }

  .field-hint-text {
    font-size: 12px;
    color: #9ca3af;
    margin: 0;
  }

  .modal-error {
    font-size: 13px;
    color: #ef4444;
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 6px;
    padding: 10px 12px;
  }

  .modal-actions {
    display: flex;
    gap: 8px;
    justify-content: flex-end;
  }

  .btn-secondary {
    padding: 9px 18px;
    background: transparent;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    color: #374151;
    cursor: pointer;
    transition: background 0.15s;
    font-family: inherit;
  }

  .btn-secondary:hover:not(:disabled) {
    background: #f3f4f6;
  }

  .btn-primary {
    padding: 9px 18px;
    background: #3b82f6;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    color: white;
    cursor: pointer;
    transition: background 0.15s;
    font-family: inherit;
  }

  .btn-primary:hover:not(:disabled) {
    background: #2563eb;
  }

  .btn-primary:disabled,
  .btn-secondary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* Success state */
  .invite-success {
    text-align: center;
  }

  .success-icon {
    font-size: 2.5rem;
    margin-bottom: 8px;
  }

  .success-text {
    font-size: 14px;
    color: #374151;
    margin: 0 0 12px;
  }

  .url-row {
    display: flex;
    gap: 8px;
    margin-bottom: 8px;
  }

  .url-input {
    flex: 1;
    padding: 9px 12px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 13px;
    color: #374151;
    background: #f9fafb;
    font-family: monospace;
    min-width: 0;
    outline: none;
  }

  .copy-btn {
    padding: 9px 14px;
    background: #3b82f6;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.15s;
    font-family: inherit;
    flex-shrink: 0;
  }

  .copy-btn.copied {
    background: #10b981;
  }

  .expires-note {
    font-size: 12px;
    color: #9ca3af;
    margin: 0;
  }
</style>
